import { SessionApiError, apiGet, apiSend } from '../../session/client';
import { type ApplicationPrefill } from './partner-model';

/**
 * Everything the Partner with us page reads and writes: the platform's own
 * provider onboarding (`provider-applications`), the routes the partner portal
 * runs its journey on. The application, its reference and its status are the
 * platform's; nothing here makes one up.
 */

export interface ProviderApplication {
  readonly id: string;
  readonly reference: string;
  readonly organisationType: string;
  readonly status: string;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly submittedAt?: string;
}

interface SectionView {
  readonly key: string;
  readonly version: number;
}

interface ApplicationDetail {
  readonly application: ProviderApplication;
  readonly sections: readonly SectionView[];
}

/** The signed-in person's own applications. */
export async function myApplications(): Promise<readonly ProviderApplication[]> {
  const data = await apiGet<{ applications: readonly ProviderApplication[] }>('/provider-applications');
  return data.applications ?? [];
}

/** The application the partner portal would open: the newest that is not withdrawn. */
export function liveApplication(
  applications: readonly ProviderApplication[],
): ProviderApplication | undefined {
  return [...applications]
    .filter((application) => application.status !== 'WITHDRAWN')
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0];
}

export interface CreatedApplication {
  readonly application: ProviderApplication;
  /** Sections or the branch that did not save. The application itself exists either way. */
  readonly unsaved: readonly string[];
}

/**
 * Starts the application and prefills it from the form.
 *
 * The start is the only step that must succeed. A prefill that is refused
 * afterwards does not undo it: the applicant has a real application with a
 * real reference, and finishes those answers in the partner portal. Which ones
 * did not save is returned so the page can say so.
 */
export async function createPrefilledApplication(prefill: ApplicationPrefill): Promise<CreatedApplication> {
  const started = await apiSend<ProviderApplication>('POST', '/provider-applications', prefill.start);
  const unsaved: string[] = [];

  let versions = new Map<string, number>();
  try {
    const detail = await apiGet<ApplicationDetail>(
      `/provider-applications/${encodeURIComponent(started.id)}`,
    );
    versions = new Map(detail.sections.map((section) => [section.key, section.version]));
  } catch {
    // Versions unknown: every section starts at 1, which is what the save path expects.
  }

  for (const section of prefill.sections) {
    try {
      await apiSend(
        'PUT',
        `/provider-applications/${encodeURIComponent(started.id)}/sections/${section.key}`,
        {
          answers: section.answers,
          complete: section.complete,
          expectedVersion: versions.get(section.key) ?? 1,
        },
      );
    } catch (error) {
      if (!(error instanceof SessionApiError)) throw error;
      unsaved.push(section.key);
    }
  }

  try {
    await apiSend(
      'POST',
      `/provider-applications/${encodeURIComponent(started.id)}/branches`,
      prefill.branch,
    );
  } catch (error) {
    if (!(error instanceof SessionApiError)) throw error;
    unsaved.push('LOCATIONS');
  }

  // The status after the prefill (saving sections moves a draft on).
  let application = started;
  try {
    application = (
      await apiGet<ApplicationDetail>(`/provider-applications/${encodeURIComponent(started.id)}`)
    ).application;
  } catch {
    application = started;
  }

  return { application, unsaved };
}

/**
 * Emails a link to set a password.
 *
 * The partner portal signs in with a password, and an account made with an
 * email code has none. This is identity's own recovery request; it answers the
 * same whatever the address, and the link it sends sets the password.
 */
export async function requestPasswordLink(email: string): Promise<void> {
  await apiSend('POST', '/identity/forgot-password', { email });
}
